import type {
    MarkdownEditorHistoryMoveConstantType,
    MarkdownEditorHistoryPushConstantType,
    MarkdownEditorTextStyleConstantType,
    MarkdownEditorAlignmentConstantType,
    MarkdownEditorPositionConstantType,
    MarkdownEditorInlineConstantType,
    MarkdownEditorSpansConstantType,
    MarkdownEditorLinesConstantType,
    MarkdownEditorImageConstantType,
    MarkdownEditorTextConstantType,
    MarkdownEditorListConstantType,
    MarkdownEditorVariantKeyType,
    MarkdownEditorActiveFormats,
    MarkdownEditorInlineFormat,
    MarkdownEditorInlineSpan,
    MarkdownEditorFenceRange,
    MarkdownEditorTextStyle,
    MarkdownEditorLineRange,
    MarkdownEditorSelection,
    MarkdownEditorSnapshot,
    MarkdownEditorListKind,
    MarkdownEditorHistory,
    MarkdownEditorStats,
    MarkdownEditorMode,
    MarkdownEditorEdit
} from "./type";

export const MARKDOWN_EDITOR_MODES: Array<MarkdownEditorMode> = [
    "live",
    "write",
    "preview"
];

export const MARKDOWN_EDITOR_INLINE_MARKERS: Record<MarkdownEditorInlineFormat, {
    close: string;
    open: string;
}> = {
    underline: {
        close: "</u>",
        open: "<u>"
    },
    bold: {
        close: "**",
        open: "**"
    },
    strikethrough: {
        close: "~~",
        open: "~~"
    },
    code: {
        close: "`",
        open: "`"
    },
    italic: {
        close: "*",
        open: "*"
    }
};

export const MARKDOWN_EDITOR_HEADING_LEVELS: Record<string, number> = {
    h1: 1,
    h2: 2,
    h3: 3,
    h4: 4,
    h5: 5,
    h6: 6
};

export const MARKDOWN_EDITOR_TEXT_STYLE_ITEMS: Array<{
    variantKey: MarkdownEditorVariantKeyType;
    value: MarkdownEditorTextStyle;
}> = [
    {
        variantKey: "# ",
        value: "h1"
    },
    {
        variantKey: "## ",
        value: "h2"
    },
    {
        variantKey: "### ",
        value: "h3"
    },
    {
        value: "paragraph",
        variantKey: "<p>"
    },
    {
        variantKey: "```",
        value: "code"
    }
];

export const MARKDOWN_EDITOR_HISTORY_LIMIT = 200;

const MARKDOWN_EDITOR_FENCE = "```";

const MARKDOWN_EDITOR_HEADING_PATTERN = /^ {0,3}(#{1,6}) /;

const MARKDOWN_EDITOR_BULLET_PATTERN = /^(\s*)[-*+] /;

const MARKDOWN_EDITOR_ORDERED_PATTERN = /^(\s*)\d+\. /;

const MARKDOWN_EDITOR_LIST_PATTERN = /^(\s*)(?:[-*+]|\d+\.) /;

const MARKDOWN_EDITOR_QUOTE_PATTERN = /^(\s*)> ?/;

const MARKDOWN_EDITOR_FENCE_PATTERN = /^\s*```/;

export const getMarkdownEditorMode = (value: string): MarkdownEditorMode | undefined => {
    return MARKDOWN_EDITOR_MODES.find((mode) => mode === value);
};

export const getMarkdownEditorHTMLElement = (node: unknown): HTMLElement | null => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

const getClampedSelection = ({
    selection,
    text
}: MarkdownEditorTextConstantType): MarkdownEditorSelection => {
    const start = Math.max(0, Math.min(selection.start, text.length));

    const end = Math.max(start, Math.min(selection.end, text.length));

    return {
        start,
        end
    };
};

export const getMarkdownEditorLineRange = ({
    position,
    text
}: MarkdownEditorPositionConstantType): MarkdownEditorLineRange => {
    const clampedPosition = Math.max(0, Math.min(position, text.length));

    const start = clampedPosition === 0 ? 0 : text.lastIndexOf("\n", clampedPosition - 1) + 1;

    const lineEnd = text.indexOf("\n", clampedPosition);

    return {
        end: lineEnd === -1 ? text.length : lineEnd,
        start
    };
};

export const getMarkdownEditorStats = ({
    position,
    text
}: MarkdownEditorPositionConstantType): MarkdownEditorStats => {
    const trimmed = text.trim();

    const clampedPosition = Math.max(0, Math.min(position, text.length));

    const {
        start
    } = getMarkdownEditorLineRange({
        position: clampedPosition,
        text
    });

    return {
        line: text.slice(0, clampedPosition).split("\n").length,
        words: trimmed === "" ? 0 : trimmed.split(/\s+/).length,
        column: clampedPosition - start + 1
    };
};

export const getMarkdownEditorInlineSpans = ({
    offset = 0,
    text
}: MarkdownEditorSpansConstantType): Array<MarkdownEditorInlineSpan> => {
    const spans: Array<MarkdownEditorInlineSpan> = [];

    const addSpan = (format: MarkdownEditorInlineFormat, start: number, innerStart: number, innerEnd: number, end: number) => {
        spans.push({
            innerStart: innerStart + offset,
            innerEnd: innerEnd + offset,
            start: start + offset,
            end: end + offset,
            format
        });
    };

    const addNested = (from: number, to: number) => {
        spans.push(...getMarkdownEditorInlineSpans({
            text: text.slice(from, to),
            offset: offset + from
        }));
    };

    let index = 0;

    while(index < text.length) {
        if(text.startsWith("`", index)) {
            const close = text.indexOf("`", index + 1);

            if(close !== -1) {
                addSpan("code", index, index + 1, close, close + 1);

                index = close + 1;

                continue;
            }
        }

        if(text.startsWith("<u>", index)) {
            const close = text.indexOf("</u>", index + 3);

            if(close !== -1) {
                addSpan("underline", index, index + 3, close, close + 4);

                addNested(index + 3, close);

                index = close + 4;

                continue;
            }
        }

        if(text.startsWith("***", index)) {
            const close = text.indexOf("***", index + 3);

            if(close !== -1) {
                addSpan("bold", index, index + 2, close + 1, close + 3);

                addSpan("italic", index + 2, index + 3, close, close + 1);

                addNested(index + 3, close);

                index = close + 3;

                continue;
            }
        }

        if(text.startsWith("**", index)) {
            const close = text.indexOf("**", index + 2);

            if(close !== -1) {
                addSpan("bold", index, index + 2, close, close + 2);

                addNested(index + 2, close);

                index = close + 2;

                continue;
            }
        }

        if(text.startsWith("~~", index)) {
            const close = text.indexOf("~~", index + 2);

            if(close !== -1) {
                addSpan("strikethrough", index, index + 2, close, close + 2);

                addNested(index + 2, close);

                index = close + 2;

                continue;
            }
        }

        if(text[index] === "*") {
            let close = index + 1;

            while(close < text.length) {
                if(text[close] !== "*") {
                    close += 1;

                    continue;
                }

                if(text[close + 1] === "*") {
                    close += 2;

                    continue;
                }

                break;
            }

            if(close < text.length && close > index + 1) {
                addSpan("italic", index, index + 1, close, close + 1);

                addNested(index + 1, close);

                index = close + 1;

                continue;
            }
        }

        index += 1;
    }

    return spans;
};

const getContainingSpan = ({
    selection,
    format,
    text
}: MarkdownEditorInlineConstantType): MarkdownEditorInlineSpan | undefined => {
    const {
        start,
        end
    } = getMarkdownEditorLineRange({
        position: selection.start,
        text
    });

    const spans = getMarkdownEditorInlineSpans({
        text: text.slice(start, end),
        offset: start
    });

    return spans.find((span) => {
        return span.format === format && selection.start >= span.innerStart && selection.end <= span.innerEnd;
    });
};

const getFenceRanges = ({
    position,
    text
}: MarkdownEditorPositionConstantType): MarkdownEditorFenceRange | null => {
    const lines = text.split("\n");

    let offset = 0;

    let openStart = -1;

    let openEnd = -1;

    for(let index = 0; index < lines.length; index += 1) {
        const line = lines[index] ?? "";

        const lineStart = offset;

        const lineEnd = offset + line.length;

        offset = lineEnd + 1;

        if(!MARKDOWN_EDITOR_FENCE_PATTERN.test(line)) {
            continue;
        }

        if(openStart === -1) {
            openStart = lineStart;

            openEnd = lineEnd;

            continue;
        }

        if(position >= openStart && position <= lineEnd) {
            return {
                closeStart: lineStart,
                closeEnd: lineEnd,
                openStart,
                openEnd
            };
        }

        openStart = -1;

        openEnd = -1;
    }

    return null;
};

const getIsInsideCodeBlock = ({
    position,
    text
}: MarkdownEditorPositionConstantType): boolean => {
    return getFenceRanges({
        position,
        text
    }) !== null;
};

export const getMarkdownEditorActiveFormats = ({
    selection,
    text
}: MarkdownEditorTextConstantType): MarkdownEditorActiveFormats => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const {
        start,
        end
    } = getMarkdownEditorLineRange({
        position: currentSelection.start,
        text
    });

    const line = text.slice(start, end);

    const headingMatch = line.match(MARKDOWN_EDITOR_HEADING_PATTERN);

    const isCodeBlock = getIsInsideCodeBlock({
        position: currentSelection.start,
        text
    });

    const inline = (Object.keys(MARKDOWN_EDITOR_INLINE_MARKERS) as Array<MarkdownEditorInlineFormat>).filter((format) => {
        return !isCodeBlock && !!getContainingSpan({
            selection: currentSelection,
            format,
            text
        });
    });

    const getList = (): MarkdownEditorListKind | null => {
        if(isCodeBlock) {
            return null;
        }

        if(MARKDOWN_EDITOR_BULLET_PATTERN.test(line)) {
            return "bullet";
        }

        if(MARKDOWN_EDITOR_ORDERED_PATTERN.test(line)) {
            return "ordered";
        }

        return null;
    };

    const getTextStyle = (): MarkdownEditorActiveFormats["textStyle"] => {
        if(isCodeBlock) {
            return "code";
        }

        if(headingMatch?.[1]) {
            return `h${headingMatch[1].length}` as MarkdownEditorActiveFormats["textStyle"];
        }

        return "paragraph";
    };

    return {
        isQuote: !isCodeBlock && MARKDOWN_EDITOR_QUOTE_PATTERN.test(line),
        textStyle: getTextStyle(),
        list: getList(),
        inline
    };
};

const getWrappedPiece = (piece: string, open: string, close: string) => {
    const trimmedStart = piece.length - piece.trimStart().length;

    const content = piece.trim();

    if(content === "") {
        return piece;
    }

    return `${piece.slice(0, trimmedStart)}${open}${content}${close}${piece.slice(trimmedStart + content.length)}`;
};

export const toggleMarkdownEditorInline = ({
    selection,
    format,
    text
}: MarkdownEditorInlineConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const {
        close,
        open
    } = MARKDOWN_EDITOR_INLINE_MARKERS[format];

    const span = getContainingSpan({
        selection: currentSelection,
        format,
        text
    });

    if(span) {
        const openLength = span.innerStart - span.start;

        return {
            text: `${text.slice(0, span.start)}${text.slice(span.innerStart, span.innerEnd)}${text.slice(span.end)}`,
            selection: {
                start: Math.max(span.start, currentSelection.start - openLength),
                end: Math.max(span.start, currentSelection.end - openLength)
            }
        };
    }

    if(currentSelection.start === currentSelection.end) {
        return {
            text: `${text.slice(0, currentSelection.start)}${open}${close}${text.slice(currentSelection.end)}`,
            selection: {
                start: currentSelection.start + open.length,
                end: currentSelection.start + open.length
            }
        };
    }

    const selected = text.slice(currentSelection.start, currentSelection.end);

    const pieces = selected.split("\n");

    const wrapped = pieces.map((piece) => getWrappedPiece(piece, open, close)).join("\n");

    const firstPiece = pieces[0] ?? "";

    const lastPiece = pieces[pieces.length - 1] ?? "";

    const startOffset = firstPiece.trim() === "" ? 0 : firstPiece.length - firstPiece.trimStart().length + open.length;

    const endOffset = lastPiece.trim() === "" ? 0 : lastPiece.length - lastPiece.trimEnd().length + close.length;

    return {
        text: `${text.slice(0, currentSelection.start)}${wrapped}${text.slice(currentSelection.end)}`,
        selection: {
            end: currentSelection.start + wrapped.length - endOffset,
            start: currentSelection.start + startOffset
        }
    };
};

const transformMarkdownEditorLines = ({
    isIncludeEmpty = false,
    selection,
    transform,
    text
}: MarkdownEditorLinesConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const first = getMarkdownEditorLineRange({
        position: currentSelection.start,
        text
    });

    const endPosition = currentSelection.end > currentSelection.start && text[currentSelection.end - 1] === "\n" ? currentSelection.end - 1 : currentSelection.end;

    const last = getMarkdownEditorLineRange({
        position: endPosition,
        text
    });

    const lines = text.slice(first.start, last.end).split("\n");

    let firstDelta = 0;

    let totalDelta = 0;

    const nextLines = lines.map((line, index) => {
        if(line.trim() === "" && !isIncludeEmpty) {
            return line;
        }

        const nextLine = transform(line, index);

        const delta = nextLine.length - line.length;

        if(index === 0) {
            firstDelta = delta;
        }

        totalDelta += delta;

        return nextLine;
    });

    const nextStart = Math.max(first.start, currentSelection.start + firstDelta);

    return {
        text: `${text.slice(0, first.start)}${nextLines.join("\n")}${text.slice(last.end)}`,
        selection: {
            end: Math.max(nextStart, currentSelection.end + totalDelta),
            start: nextStart
        }
    };
};

export const toggleMarkdownEditorCodeBlock = ({
    selection,
    text
}: MarkdownEditorTextConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const fence = getFenceRanges({
        position: currentSelection.start,
        text
    });

    if(fence) {
        const removedOpen = fence.openEnd - fence.openStart + 1;

        const mapPosition = (position: number) => {
            if(position <= fence.openEnd + 1) {
                return fence.openStart;
            }

            if(position < fence.closeStart) {
                return position - removedOpen;
            }

            return fence.closeStart - 1 - removedOpen;
        };

        return {
            text: `${text.slice(0, fence.openStart)}${text.slice(fence.openEnd + 1, fence.closeStart - 1)}${text.slice(fence.closeEnd)}`,
            selection: {
                start: mapPosition(currentSelection.start),
                end: mapPosition(currentSelection.end)
            }
        };
    }

    const first = getMarkdownEditorLineRange({
        position: currentSelection.start,
        text
    });

    const last = getMarkdownEditorLineRange({
        position: currentSelection.end > currentSelection.start && text[currentSelection.end - 1] === "\n" ? currentSelection.end - 1 : currentSelection.end,
        text
    });

    const opening = `${MARKDOWN_EDITOR_FENCE}\n`;

    return {
        text: `${text.slice(0, first.start)}${opening}${text.slice(first.start, last.end)}\n${MARKDOWN_EDITOR_FENCE}${text.slice(last.end)}`,
        selection: {
            start: currentSelection.start + opening.length,
            end: currentSelection.end + opening.length
        }
    };
};

export const applyMarkdownEditorTextStyle = ({
    selection,
    textStyle,
    text
}: MarkdownEditorTextStyleConstantType): MarkdownEditorEdit => {
    const active = getMarkdownEditorActiveFormats({
        selection,
        text
    });

    if(textStyle === "code") {
        return toggleMarkdownEditorCodeBlock({
            selection,
            text
        });
    }

    const base = active.textStyle === "code" ? toggleMarkdownEditorCodeBlock({
        selection,
        text
    }) : {
        selection,
        text
    };

    const level = MARKDOWN_EDITOR_HEADING_LEVELS[textStyle] ?? 0;

    return transformMarkdownEditorLines({
        transform: (line) => {
            const stripped = line.replace(MARKDOWN_EDITOR_HEADING_PATTERN, "");

            return level === 0 ? stripped : `${"#".repeat(level)} ${stripped}`;
        },
        selection: base.selection,
        isIncludeEmpty: true,
        text: base.text
    });
};

export const toggleMarkdownEditorList = ({
    selection,
    kind,
    text
}: MarkdownEditorListConstantType): MarkdownEditorEdit => {
    const pattern = kind === "bullet" ? MARKDOWN_EDITOR_BULLET_PATTERN : MARKDOWN_EDITOR_ORDERED_PATTERN;

    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const first = getMarkdownEditorLineRange({
        position: currentSelection.start,
        text
    });

    const last = getMarkdownEditorLineRange({
        position: currentSelection.end > currentSelection.start && text[currentSelection.end - 1] === "\n" ? currentSelection.end - 1 : currentSelection.end,
        text
    });

    const lines = text.slice(first.start, last.end).split("\n").filter((line) => line.trim() !== "");

    const isAllListed = lines.length > 0 && lines.every((line) => pattern.test(line));

    let order = 0;

    return transformMarkdownEditorLines({
        transform: (line) => {
            const stripped = line.replace(MARKDOWN_EDITOR_LIST_PATTERN, "$1");

            if(isAllListed) {
                return stripped;
            }

            order += 1;

            const indent = stripped.match(/^\s*/)?.[0] ?? "";

            const marker = kind === "bullet" ? "- " : `${order}. `;

            return `${indent}${marker}${stripped.slice(indent.length)}`;
        },
        isIncludeEmpty: lines.length === 0,
        selection: currentSelection,
        text
    });
};

export const insertMarkdownEditorLink = ({
    selection,
    text
}: MarkdownEditorTextConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const selected = text.slice(currentSelection.start, currentSelection.end);

    const caret = currentSelection.start + (selected === "" ? 1 : selected.length + 3);

    return {
        text: `${text.slice(0, currentSelection.start)}[${selected}]()${text.slice(currentSelection.end)}`,
        selection: {
            start: caret,
            end: caret
        }
    };
};

export const toggleMarkdownEditorQuote = ({
    selection,
    text
}: MarkdownEditorTextConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const first = getMarkdownEditorLineRange({
        position: currentSelection.start,
        text
    });

    const last = getMarkdownEditorLineRange({
        position: currentSelection.end > currentSelection.start && text[currentSelection.end - 1] === "\n" ? currentSelection.end - 1 : currentSelection.end,
        text
    });

    const lines = text.slice(first.start, last.end).split("\n").filter((line) => line.trim() !== "");

    const isAllQuoted = lines.length > 0 && lines.every((line) => MARKDOWN_EDITOR_QUOTE_PATTERN.test(line));

    return transformMarkdownEditorLines({
        transform: (line) => {
            if(isAllQuoted) {
                return line.replace(MARKDOWN_EDITOR_QUOTE_PATTERN, "$1");
            }

            return MARKDOWN_EDITOR_QUOTE_PATTERN.test(line) ? line : `> ${line}`;
        },
        isIncludeEmpty: lines.length === 0,
        selection: currentSelection,
        text
    });
};

export const insertMarkdownEditorImage = ({
    selection,
    image,
    text
}: MarkdownEditorImageConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const alt = image.alt ?? text.slice(currentSelection.start, currentSelection.end);

    const markup = `![${alt}](${image.url})`;

    const caret = currentSelection.start + markup.length;

    return {
        text: `${text.slice(0, currentSelection.start)}${markup}${text.slice(currentSelection.end)}`,
        selection: {
            start: caret,
            end: caret
        }
    };
};

export const applyMarkdownEditorAlignment = ({
    alignment,
    selection,
    text
}: MarkdownEditorAlignmentConstantType): MarkdownEditorEdit => {
    const currentSelection = getClampedSelection({
        selection,
        text
    });

    const selected = text.slice(currentSelection.start, currentSelection.end);

    const open = `<${alignment}>\n`;

    const close = `\n</${alignment}>`;

    const caretStart = currentSelection.start + open.length;

    return {
        text: `${text.slice(0, currentSelection.start)}${open}${selected}${close}${text.slice(currentSelection.end)}`,
        selection: {
            end: caretStart + selected.length,
            start: caretStart
        }
    };
};

export const createMarkdownEditorHistory = (): MarkdownEditorHistory => {
    return {
        isTyping: false,
        lastTime: 0,
        future: [],
        past: []
    };
};

export const pushMarkdownEditorHistory = ({
    typingWindow,
    isTyping,
    snapshot,
    history,
    now
}: MarkdownEditorHistoryPushConstantType) => {
    const isCoalesced = isTyping && history.isTyping && history.past.length > 0 && now - history.lastTime < typingWindow;

    history.lastTime = now;

    history.isTyping = isTyping;

    history.future = [];

    if(isCoalesced) {
        return;
    }

    history.past.push(snapshot);

    if(history.past.length > MARKDOWN_EDITOR_HISTORY_LIMIT) {
        history.past.shift();
    }
};

export const undoMarkdownEditorHistory = ({
    current,
    history
}: MarkdownEditorHistoryMoveConstantType): MarkdownEditorSnapshot | undefined => {
    const snapshot = history.past.pop();

    if(!snapshot) {
        return undefined;
    }

    history.future.push(current);

    history.isTyping = false;

    return snapshot;
};

export const redoMarkdownEditorHistory = ({
    current,
    history
}: MarkdownEditorHistoryMoveConstantType): MarkdownEditorSnapshot | undefined => {
    const snapshot = history.future.pop();

    if(!snapshot) {
        return undefined;
    }

    history.past.push(current);

    history.isTyping = false;

    return snapshot;
};

export const getMarkdownEditorToolbarItems = (element: HTMLElement): Array<HTMLElement> => {
    return Array.from(element.querySelectorAll<HTMLElement>("button, [role=\"button\"]")).filter((item) => {
        return !item.matches(":disabled") && item.getAttribute("aria-disabled") !== "true";
    });
};

export const applyMarkdownEditorToolbarTabStop = (element: HTMLElement, focused: HTMLElement | null) => {
    const items = getMarkdownEditorToolbarItems(element);

    const stop = items.find((item) => item === focused) ?? items[0];

    items.forEach((item) => {
        item.tabIndex = item === stop ? 0 : -1;
    });
};
